import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { StarRating } from '@/components/StarRating';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, CalendarCheck, Loader2, X } from 'lucide-react';
import { format as formatDate } from 'date-fns';
import { cn } from '@/lib/utils';
import { VoiceReview } from '@/components/VoiceReview';
import { TagSelector } from '@/components/TagSelector';
import { ReadingFormatSelector } from '@/components/ReadingFormatSelector';
import { supabase } from '@/integrations/supabase/client';
import { getUserDefaultFormat, ReadingFormat } from '@/lib/readingFormatUtils';
import { useSuspensionCheck } from '@/hooks/useSuspensionCheck';
import { SuspensionBanner } from '@/components/SuspensionBanner';

interface QuickReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  book: {
    id: string;
    title: string;
    author: string;
  };
  initialData?: {
    rating?: number;
    reviewText?: string;
    finishedAt?: string;
    startedAt?: string;
    tags?: string[];
    reading_format?: ReadingFormat;
  };
  onSubmit: (data: {
    rating: number;
    reviewText: string;
    finishedAt?: Date;
    startedAt?: Date;
    tags?: string[];
    reading_format?: ReadingFormat;
  }) => Promise<void>;
}

export function QuickReviewDialog({ 
  isOpen, 
  onClose, 
  book, 
  initialData,
  onSubmit 
}: QuickReviewDialogProps) {
  const { suspensionInfo, loading: suspensionLoading } = useSuspensionCheck();
  const [rating, setRating] = useState<number>(initialData?.rating || 0);
  const [reviewText, setReviewText] = useState<string>(initialData?.reviewText || '');
  const [finishedAt, setFinishedAt] = useState<Date | undefined>(
    initialData?.finishedAt ? new Date(initialData.finishedAt) : undefined
  );
  const [startedAt, setStartedAt] = useState<Date | undefined>(
    initialData?.startedAt ? new Date(initialData.startedAt) : undefined
  );
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  const [format, setFormat] = useState<ReadingFormat>(initialData?.reading_format || 'print');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useVoiceReview, setUseVoiceReview] = useState(false);
  const [startedMonth, setStartedMonth] = useState<Date | undefined>(
    initialData?.startedAt ? new Date(initialData.startedAt) : undefined
  );
  const [finishedMonth, setFinishedMonth] = useState<Date | undefined>(
    initialData?.finishedAt ? new Date(initialData.finishedAt) : undefined
  );
  const prevIsOpenRef = useRef(isOpen);

  // Load user's default format setting
  useEffect(() => {
    const loadDefaults = async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      // Load default reading format
      if (!initialData?.reading_format) {
        const defaultFormat = await getUserDefaultFormat(userData.user.id);
        if (defaultFormat) {
          setFormat(defaultFormat);
        }
      }
    };

    if (isOpen) {
      loadDefaults();
    }
  }, [isOpen, initialData?.reading_format]);

  // Reset form when dialog transitions from closed to open
  useEffect(() => {
    const wasClosedNowOpen = !prevIsOpenRef.current && isOpen;
    prevIsOpenRef.current = isOpen;
    
    if (wasClosedNowOpen) {
      // Dialog just opened - initialize from initialData
      setRating(initialData?.rating || 0);
      setReviewText(initialData?.reviewText || '');
      setFinishedAt(initialData?.finishedAt ? new Date(initialData.finishedAt) : undefined);
      setStartedAt(initialData?.startedAt ? new Date(initialData.startedAt) : undefined);
      setTags(initialData?.tags || []);
      setFormat(initialData?.reading_format || 'print');
      setStartedMonth(initialData?.startedAt ? new Date(initialData.startedAt) : undefined);
      setFinishedMonth(initialData?.finishedAt ? new Date(initialData.finishedAt) : undefined);
    }
  }, [isOpen, initialData]);

  const handleSubmit = async () => {
    if (rating === 0 && !reviewText.trim()) {
      return;
    }
    
    // Prevent submission if suspended
    if (suspensionInfo.isSuspended) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        rating,
        reviewText: reviewText.trim(),
        finishedAt,
        startedAt,
        tags,
        reading_format: format,
      });

      // Update streak for meaningful reading activity
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        supabase.functions.invoke('update-streak', {
          body: { userId: userData.user.id }
        }).catch(err => console.error('Error updating streak:', err));
      }

      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form state when closing
      setRating(0);
      setReviewText('');
      setFinishedAt(undefined);
      setStartedAt(undefined);
      setTags([]);
      setFormat('print');
      setUseVoiceReview(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review this book</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{book.title}</span>
            <br />
            <span className="text-sm text-muted-foreground">by {book.author}</span>
          </DialogDescription>
        </DialogHeader>
        
        {/* Suspension Banner */}
        {!suspensionLoading && <SuspensionBanner suspensionInfo={suspensionInfo} />}
        
        <div className="space-y-6 py-4">
          {/* Rating */}
          <div className="space-y-2">
            <Label>Rating</Label>
            <div className="flex justify-center">
              <StarRating
                value={rating}
                onChange={setRating}
                size="lg"
                showLabel={false}
              />
            </div>
          </div>

          {/* Reading Format Selector */}
          <ReadingFormatSelector
            value={format}
            onChange={setFormat}
          />

          {/* Review Text */}
          <div className="space-y-2">
            <Label htmlFor="review-text">Your Review (Optional)</Label>
            <div className="flex items-start gap-2 px-3 py-2 bg-muted/50 rounded-md border border-border/50 mb-2">
              <span className="text-lg flex-shrink-0 mt-0.5">🦖</span>
              <p className="text-xs text-muted-foreground">
                Remember: Dino's for honest, respectful reviews. Focus on the book, not the author.
              </p>
            </div>
            <Textarea
              id="review-text"
              placeholder="What did you think of this book? Share your thoughts..."
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              rows={6}
              className="resize-none"
            />
          </div>

          {/* Voice Review (Optional) */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch
                id="use-voice-review"
                checked={useVoiceReview}
                onCheckedChange={setUseVoiceReview}
              />
              <Label htmlFor="use-voice-review">Use voice to draft review (optional)</Label>
            </div>
            {useVoiceReview && (
              <VoiceReview
                bookTitle={book.title}
                bookAuthor={book.author}
                onReviewGenerated={(generatedReview) => {
                  console.log('Voice review generated:', generatedReview);
                  setReviewText(generatedReview);
                }}
              />
            )}
          </div>

          {/* Date Started */}
          <div className="space-y-2">
            <Label>Date Started (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startedAt && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startedAt ? formatDate(startedAt, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startedAt}
                  onSelect={setStartedAt}
                  month={startedMonth}
                  onMonthChange={setStartedMonth}
                  initialFocus
                  disabled={(date) => date > new Date()}
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Finished */}
          <div className="space-y-2">
            <Label>Date Finished (Optional)</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !finishedAt && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {finishedAt ? formatDate(finishedAt, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={finishedAt}
                    onSelect={setFinishedAt}
                    month={finishedMonth}
                    onMonthChange={setFinishedMonth}
                    initialFocus
                    disabled={(date) => {
                      if (date > new Date()) return true;
                      if (startedAt && date < startedAt) return true;
                      return false;
                    }}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {!finishedAt ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="default"
                  onClick={() => setFinishedAt(new Date())}
                  className="shrink-0"
                >
                  <CalendarCheck className="w-4 h-4 mr-1" />
                  Today
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setFinishedAt(undefined)}
                  className="shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Tag Selector */}
          <TagSelector
            selectedTags={tags}
            onChange={setTags}
            maxTags={5}
          />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={rating === 0 || isSubmitting || suspensionInfo.isSuspended}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : suspensionInfo.isSuspended ? (
                'Cannot Post While Suspended'
              ) : (
                reviewText.trim() ? 'Complete Review' : 'Save Rating'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
